import { type FC } from 'react';
import { merge } from 'lodash';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  type DialogProps,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import type { FormProps } from '#packages/web/types/form-props';
import type { MutationState } from '#packages/web/types/generic-mutation-view-model';

import type { NewAudit } from '../../models/audit';
import { AuditTypeChip } from '../audit-type-chip';
import { AuditTypeIcon } from '../audit-type-icon';

export type CreateAuditDialogProps = DialogProps &
  FormProps<NewAudit> & {
    /**
     * The mutation state of the create audit dialog.
     */
    state: MutationState;
  };

export const CreateAuditDialog: FC<CreateAuditDialogProps> = ({
  /* FormProps */
  initialValue,
  onFormCancel,
  onFormSubmit,
  state,

  /* MUI props */
  ...props
}) => {
  const { control, handleSubmit, formState } = useForm<NewAudit>({
    defaultValues: merge(
      // ensure types is always defined
      { types: [] } satisfies Partial<NewAudit>,
      initialValue,
    ),
  });

  const { errors } = formState;

  const isLoading = state === 'mutating';
  const isDismissable = !isLoading; // prevent dismissing the form while submitting

  const disableForm = isLoading;

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      {...props}
      // Ensure the dialog acts as a form
      onSubmit={handleSubmit(onFormSubmit)}
      component="form">
      <DialogTitle>New Audit</DialogTitle>
      <DialogContent>
        <Stack spacing={4} sx={{ mt: 2 }}>
          <Controller
            control={control}
            name="title"
            rules={{
              required: { value: true, message: 'Title is required' },
              minLength: 1,
              maxLength: 253,
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                error={!!errors.title}
                aria-invalid={!!errors.title}>
                <TextField
                  {...field}
                  autoFocus
                  label="Title"
                  type="text"
                  variant="outlined"
                  disabled={disableForm}
                  error={!!errors.title}
                  aria-invalid={!!errors.title}
                />
                <FormHelperText>{errors.title?.message}</FormHelperText>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="types"
            rules={{
              required: { value: true, message: 'Type is required' },
              minLength: { value: 1, message: 'Select at least one type' },
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                error={!!errors.types}
                aria-invalid={!!errors.types}>
                <InputLabel>Type</InputLabel>
                <Select
                  disabled={disableForm}
                  multiple
                  error={!!errors.types}
                  aria-invalid={!!errors.types}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  input={
                    <OutlinedInput
                      label="Type"
                      error={!!errors.types}
                      aria-errormessage={errors.types?.message}
                    />
                  }
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5}>
                      {selected.map((value) => (
                        <AuditTypeChip key={value} type={value} size="medium" />
                      ))}
                    </Stack>
                  )}>
                  <MenuItem value="ea">
                    <ListItemIcon>
                      <AuditTypeIcon type="ea" />
                    </ListItemIcon>
                    <ListItemText>Energy Audit (EA)</ListItemText>
                  </MenuItem>
                  <MenuItem value="bca">
                    <ListItemIcon>
                      <AuditTypeIcon type="bca" />
                    </ListItemIcon>
                    <ListItemText>
                      Building Condition Assessment (BCA)
                    </ListItemText>
                  </MenuItem>
                </Select>
                <FormHelperText>{errors.types?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="reset" onClick={onFormCancel} disabled={!isDismissable}>
          Cancel
        </Button>
        <Button loading={isLoading} type="submit">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
